import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import React, { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Field, FieldLabel } from "@volca/ui/components/ui/field";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { cn } from "@volca/ui/lib/utils";

interface ExpandableTextareaProps extends React.ComponentProps<"textarea"> {
  label?: string;
  required?: boolean;
  expandedHeight?: string;
  initialHeight?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

export const ExpandableTextarea = React.forwardRef<
  HTMLTextAreaElement,
  ExpandableTextareaProps
>(
  (
    {
      className,
      id,
      label,
      required,
      expandedHeight,
      initialHeight,
      placeholder,
      onSubmit,
      isSubmitting = false,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const textareaId = id ?? generatedId;
    const [isFocused, setIsFocused] = useState(false);

    const hasText =
      props.value &&
      typeof props.value === "string" &&
      props.value.trim().length > 0;

    const isExpanded = isSubmitting || isFocused || hasText;
    const showSubmit = Boolean(hasText && onSubmit);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "Enter" &&
        hasText &&
        onSubmit
      ) {
        e.preventDefault();
        onSubmit();
        setIsFocused(false);
      }
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    return (
      <Field data-disabled={props.disabled || isSubmitting || undefined}>
        {label && (
          <FieldLabel htmlFor={textareaId} className="gap-0.5">
            {label}
            {required && <span aria-hidden="true">*</span>}
          </FieldLabel>
        )}
        <div
          className={cn(
            "relative transition-[height] duration-300 ease-in-out",
            {
              [expandedHeight ?? ""]: isExpanded,
              [initialHeight ?? ""]: !isExpanded,
            },
          )}
        >
          <Textarea
            ref={ref}
            id={textareaId}
            required={required}
            className={cn(
              "absolute inset-0 field-sizing-fixed h-full min-h-0 resize-none",
              { "pr-12": showSubmit },
              className,
            )}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {showSubmit && (
            <Button
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                if (!isSubmitting) onSubmit?.();
              }}
              className="absolute right-2 bottom-2 rounded-full"
              aria-label="Submit note"
              aria-busy={isSubmitting || undefined}
              disabled={isSubmitting}
            >
              {!isSubmitting && <ArrowRightIcon />}
            </Button>
          )}
        </div>
      </Field>
    );
  },
);

ExpandableTextarea.displayName = "ExpandableTextarea";
